import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Navbar from '../components/Navbar';

import { API, authOpts } from '../config/api';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');
  const isRamburs = searchParams.get('ramburs') === '1';
  const authToken = searchParams.get('auth_token');

  useEffect(() => {
    // Store auth token from URL (COD/magic link flow)
    if (authToken) {
      try { localStorage.setItem('petsbox_session_token', authToken); } catch {}
    }

    // COD orders — skip Stripe check, show success directly
    if (isRamburs) {
      setStatus('success');
      if (window.fbq) {
        window.fbq('track', 'Purchase', {
          currency: 'RON',
          content_type: 'product',
          content_name: 'PetsBox Subscription COD',
        });
      }
      return;
    }
    if (!sessionId) { setStatus('failed'); return; }
    const pollStatus = async () => {
      try {
        const response = await fetch(`${API}/checkout/status/${sessionId}`, authOpts());
        if (response.ok) {
          const data = await response.json();
          if (data.payment_status === 'paid') {
            setStatus('success');
            // Fire Meta Pixel Purchase event
            if (window.fbq) {
              window.fbq('track', 'Purchase', {
                value: data.amount_total,
                currency: (data.currency || 'ron').toUpperCase(),
                content_type: 'product',
                content_name: 'PetsBox Subscription',
              });
            }
            return;
          }
          else if (data.status === 'expired') { setStatus('failed'); return; }
        }
        if (attempts < 5) setTimeout(() => setAttempts(prev => prev + 1), 2000);
        else setStatus('success');
      } catch {
        if (attempts >= 5) setStatus('failed');
        else setTimeout(() => setAttempts(prev => prev + 1), 2000);
      }
    };
    pollStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-brand-cream">
      <Navbar />
      <main className="pt-32 pb-16">
        <div className="section-container">
          <div className="max-w-md mx-auto text-center">
            {status === 'checking' && (
              <>
                <div className="w-20 h-20 bg-brand-sage/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-10 h-10 text-brand-sage animate-spin" />
                </div>
                <h1 className="font-heading text-3xl font-bold text-brand-charcoal mb-4">{t('checkout_checking')}</h1>
                <p className="text-brand-muted">{t('checkout_checking_desc')}</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 bg-brand-sage/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-brand-sage" />
                </div>
                <h1 className="font-heading text-3xl font-bold text-brand-charcoal mb-4">{t('checkout_success')}</h1>
                <p className="text-brand-muted mb-8">{t('checkout_success_desc')}</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button onClick={() => navigate('/dashboard')} className="btn-primary" data-testid="success-dashboard-btn">{t('checkout_to_profile')}</button>
                  <button onClick={() => navigate('/')} className="btn-outline">{t('checkout_to_home')}</button>
                </div>
              </>
            )}

            {status === 'failed' && (
              <>
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-10 h-10 text-red-500" />
                </div>
                <h1 className="font-heading text-3xl font-bold text-brand-charcoal mb-4">{t('checkout_failed')}</h1>
                <p className="text-brand-muted mb-8">{t('checkout_failed_desc')}</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button onClick={() => navigate('/#pricing')} className="btn-primary">{t('checkout_retry')}</button>
                  <button onClick={() => navigate('/#contact')} className="btn-outline">{t('checkout_contact')}</button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
